/**
 * Drip Settings — Scott's pre-approved auto-drip first-touch email control.
 *
 * Lives as the "Auto-Drip" tab in the Dealer Portal. Settings persist via
 * /api/dealer/drip/settings; "Send test" and "Run now" let Scott verify
 * before turning it loose nightly.
 */
import { useEffect, useState } from "react";
import { Loader2, Send, Save, FlaskConical, History, Play, Power } from "lucide-react";
import { api } from "../lib/api";

const TZ_OPTIONS = [
  "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
  "America/Phoenix", "America/Anchorage", "Pacific/Honolulu",
];
const SOURCES = [
  "Website Chatbot", "Contact Page", "BYOD", "Trade Submit", "trade-pdf-email",
];

export default function DripSettingsPanel() {
  const [s, setS] = useState(null);
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [testEmail, setTestEmail] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        api.get("/dealer/drip/settings"),
        api.get("/dealer/drip/log").catch(() => ({ data: { items: [] } })),
      ]);
      setS(a.data.settings);
      setLog(b.data.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked
            : e.target.type === "number" ? Number(e.target.value)
            : e.target.value;
    setS((cur) => ({ ...cur, [k]: v }));
  };
  const toggleSource = (src) => {
    setS((cur) => {
      const list = cur.source_filter || [];
      return { ...cur, source_filter: list.includes(src) ? list.filter((x) => x !== src) : [...list, src] };
    });
  };

  const save = async () => {
    setBusy("save"); setMsg(""); setErr("");
    try {
      await api.patch("/dealer/drip/settings", s);
      setMsg("Saved.");
      setTimeout(() => setMsg(""), 1500);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Couldn't save.");
    } finally { setBusy(""); }
  };
  const dryRun = async () => {
    setBusy("dry"); setMsg(""); setErr("");
    try {
      const { data } = await api.post("/dealer/drip/dry-run");
      setMsg(`Dry-run: would send ${data.sent} email${data.sent === 1 ? "" : "s"}.`);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Dry-run failed.");
    } finally { setBusy(""); }
  };
  const runNow = async () => {
    if (!window.confirm("This will send REAL emails to drip candidates. Continue?")) return;
    setBusy("run"); setMsg(""); setErr("");
    try {
      const { data } = await api.post("/dealer/drip/run-now");
      setMsg(`Sent ${data.sent}, ${data.failures} failure${data.failures === 1 ? "" : "s"}, ${data.skipped} skipped.`);
      await load();
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Run failed.");
    } finally { setBusy(""); }
  };
  const sendTest = async () => {
    if (!testEmail) { setErr("Enter an email."); return; }
    setBusy("test"); setMsg(""); setErr("");
    try {
      await api.post("/dealer/drip/send-test", { ...s, send_test_to: testEmail });
      setMsg(`Test sent to ${testEmail}.`);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Test send failed.");
    } finally { setBusy(""); }
  };

  if (loading || !s) return <div className="text-white/60 p-6">Loading drip settings…</div>;

  return (
    <div className="space-y-4" data-testid="drip-settings">
      {/* Header card with toggle */}
      <div className="rounded-2xl border border-white/15 bg-white/5 p-4 flex items-start gap-3 flex-wrap" data-testid="drip-toggle-card">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-extrabold uppercase tracking-widest text-[var(--ssc-blue)] inline-flex items-center gap-1.5">
            <Power size={11} /> Auto-Drip Status
          </div>
          <div className="text-white font-black text-lg mt-0.5">
            {s.enabled ? "On — runs daily" : "Off"}
          </div>
          {s.last_run_at && (
            <div className="text-xs text-white/60 mt-0.5">
              Last run {new Date(s.last_run_at).toLocaleString()} · sent {s.last_run_sent || 0}
              {typeof s.last_run_failures === "number" ? ` · ${s.last_run_failures} failures` : ""}
            </div>
          )}
        </div>
        <label className="inline-flex items-center gap-2 cursor-pointer text-white text-sm font-bold">
          <input type="checkbox" checked={!!s.enabled} onChange={set("enabled")} className="w-5 h-5" data-testid="drip-enabled-toggle" />
          {s.enabled ? "Pause" : "Enable"}
        </label>
      </div>

      {/* Editor */}
      <div className="rounded-2xl border border-white/15 bg-white/5 p-4 space-y-3" data-testid="drip-editor">
        <div className="text-[10px] font-extrabold uppercase tracking-widest text-white/70">Template</div>
        <Field label="Subject">
          <input className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/15 text-white" value={s.subject || ""} onChange={set("subject")} data-testid="drip-subject" />
        </Field>
        <Field label="Body (use {{first_name}}, {{vehicle_interest}} for personalisation)">
          <textarea className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/15 text-white min-h-[160px] font-mono text-sm"
                    value={s.body || ""} onChange={set("body")} data-testid="drip-body" />
        </Field>

        <div className="grid sm:grid-cols-3 gap-3">
          <Field label="Daily cap">
            <input type="number" min={1} max={500} className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/15 text-white"
                   value={s.daily_cap} onChange={set("daily_cap")} data-testid="drip-cap" />
          </Field>
          <Field label="Lead age (hours)">
            <input type="number" min={1} max={720} className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/15 text-white"
                   value={s.lead_age_hours} onChange={set("lead_age_hours")} data-testid="drip-age" />
          </Field>
          <Field label="Run hour (local)">
            <input type="number" min={0} max={23} className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/15 text-white"
                   value={s.run_hour_local} onChange={set("run_hour_local")} data-testid="drip-hour" />
          </Field>
        </div>
        <Field label="Timezone">
          <select className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/15 text-white" value={s.timezone} onChange={set("timezone")} data-testid="drip-tz">
            {TZ_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </Field>
        <Field label="Source filter (only fire for these lead sources — empty = all)">
          <div className="flex flex-wrap gap-1.5" data-testid="drip-sources">
            {SOURCES.map((src) => {
              const on = (s.source_filter || []).includes(src);
              return (
                <button key={src} type="button" onClick={() => toggleSource(src)}
                        className={`text-xs font-bold px-2.5 py-1 rounded-full transition border ${on ? "bg-[var(--ssc-blue)] text-white border-[var(--ssc-blue)]" : "bg-white/5 text-white/70 border-white/15"}`}
                        data-testid={`drip-source-${src.replace(/[^A-Za-z0-9]/g, "_")}`}>
                  {src}
                </button>
              );
            })}
          </div>
        </Field>

        {msg && <div className="text-sm text-emerald-300" data-testid="drip-msg">{msg}</div>}
        {err && <div className="text-sm text-red-300" data-testid="drip-err">{err}</div>}

        <div className="flex flex-wrap gap-2 pt-1">
          <button onClick={save} disabled={busy === "save"} className="ssc-btn ssc-btn-primary text-sm inline-flex items-center gap-1.5" data-testid="drip-save">
            {busy === "save" ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save settings
          </button>
          <button onClick={dryRun} disabled={busy === "dry"} className="ssc-btn ssc-btn-secondary text-sm inline-flex items-center gap-1.5" data-testid="drip-dry">
            {busy === "dry" ? <Loader2 size={13} className="animate-spin" /> : <FlaskConical size={13} />} Dry run
          </button>
          <button onClick={runNow} disabled={busy === "run"} className="ssc-btn ssc-btn-secondary text-sm inline-flex items-center gap-1.5" data-testid="drip-run-now">
            {busy === "run" ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />} Run now
          </button>
        </div>

        <div className="flex gap-2 mt-2 pt-3 border-t border-white/10 flex-wrap">
          <input
            type="email"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder="askscott@scottsellschevys.com"
            className="px-3 py-2 rounded-lg bg-white/10 border border-white/15 text-white text-sm flex-1 min-w-[200px]"
            data-testid="drip-test-email"
          />
          <button onClick={sendTest} disabled={busy === "test" || !testEmail} className="ssc-btn ssc-btn-secondary text-sm inline-flex items-center gap-1.5" data-testid="drip-send-test">
            {busy === "test" ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Send test
          </button>
        </div>
      </div>

      {/* Log */}
      <div className="rounded-2xl border border-white/15 bg-white/5 p-4" data-testid="drip-log">
        <div className="text-[10px] font-extrabold uppercase tracking-widest text-white/70 inline-flex items-center gap-1.5">
          <History size={11} /> Recent runs
        </div>
        {log.length === 0 ? (
          <div className="text-sm text-white/60 mt-2">No runs yet.</div>
        ) : (
          <ul className="mt-3 space-y-2">
            {log.map((r) => (
              <li key={r.id} className="rounded-xl bg-white p-3" data-testid={`drip-log-${r.id}`}>
                <div className="text-[10px] uppercase font-extrabold text-[#6b7c93] tracking-widest">{new Date(r.at).toLocaleString()}</div>
                <div className="text-sm font-bold text-[var(--ssc-navy)]">
                  Sent {r.summary?.sent || 0} · {r.summary?.failures || 0} fail · {r.summary?.skipped || 0} skipped
                  {r.summary?.manual && <span className="ml-2 text-xs font-bold text-[var(--ssc-blue)]">MANUAL</span>}
                  {r.summary?.dry_run && <span className="ml-2 text-xs font-bold text-amber-700">DRY</span>}
                </div>
                {(r.entries || []).slice(0, 5).map((e, i) => (
                  <div key={i} className="text-xs text-[#475569] mt-0.5">→ {e.to} · {e.subject?.slice(0, 60)} {e.success === false && <span className="text-red-600 font-bold">FAILED: {e.error}</span>}</div>
                ))}
                {(r.entries || []).length > 5 && <div className="text-[10px] text-[#6b7c93] mt-1">+ {(r.entries || []).length - 5} more</div>}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="block text-[10px] font-extrabold uppercase tracking-widest text-white/70 mb-1">{label}</label>
    {children}
  </div>
);
